import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { VerticalModuleProvider } from '@lipaflow/shared'
import {
  PortalLayout,
  PortalVerticalProvider,
  TokenGate,
} from '@lipaflow/portal-core'
import { RENTAL_DEFAULT_CONTEXT, rentalModule, rentalPayLinkNotice, OwnerGate, RoleGate, RoleSwitch } from '@lipaflow/rental'
import { MONEY_NAV_ITEMS } from '@lipaflow/portal-core'
import {
  HomeModernIcon,
  BuildingOffice2Icon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  WrenchScrewdriverIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'

// Money pages shared with the parent portal via @lipaflow/portal-core.
// Tenant-specific home and repair pages are supplied by the rental module.
const BalancePage = lazy(() => import('@lipaflow/portal-core/pages/BalancePage'))
const ReceiptsPage = lazy(() => import('@lipaflow/portal-core/pages/ReceiptsPage'))
const PaymentPlansPage = lazy(() => import('@lipaflow/portal-core/pages/PaymentPlansPage'))
const LandingPage = lazy(() => import('@lipaflow/portal-core/pages/LandingPage'))
const MyTenancyPage = lazy(() => import('@lipaflow/rental').then((m) => ({ default: m.MyTenancyPage })))
const MyRepairsPage = lazy(() => import('@lipaflow/rental').then((m) => ({ default: m.MyRepairsPage })))
const MyMessagesPage = lazy(() => import('@lipaflow/rental').then((m) => ({ default: m.MyMessagesPage })))

// The money tabs, then the tenant's own home (tenancy and notice).
const TENANT_NAV_ITEMS = [...MONEY_NAV_ITEMS, { to: '/home', label: 'My home', icon: HomeModernIcon }, { to: '/maintenance', label: 'Repairs', icon: WrenchScrewdriverIcon }, { to: '/messages', label: 'Messages', icon: ChatBubbleLeftRightIcon }]
const OwnerUnitsPage = lazy(() => import('@lipaflow/rental').then(m => ({ default: m.OwnerUnitsPage })))
const OwnerDecisionsPage = lazy(() => import('@lipaflow/rental').then(m => ({ default: m.OwnerDecisionsPage })))
const OwnerStatementPage = lazy(() => import('@lipaflow/rental').then(m => ({ default: m.OwnerStatementPage })))
const OwnerPayoutsPage = lazy(() => import('@lipaflow/rental').then(m => ({ default: m.OwnerPayoutsPage })))
const OwnerRepairsPage = lazy(() => import('@lipaflow/rental').then(m => ({ default: m.OwnerRepairsPage })))
const OWNER_NAV = [
  { to: '/owner', label: 'Units', icon: BuildingOffice2Icon, end: true },
  { to: '/owner/decisions', label: 'Decisions', icon: ClipboardDocumentCheckIcon },
  { to: '/owner/statement', label: 'Statement', icon: CalendarDaysIcon },
  { to: '/owner/payouts', label: 'Payouts', icon: HomeModernIcon },
  { to: '/owner/repairs', label: 'Repairs', icon: WrenchScrewdriverIcon },
]
const MagicPayPage = lazy(() => import('@lipaflow/portal-core/pages/MagicPayPage'))
const PayLinkPage = lazy(() => import('@lipaflow/portal-core/pages/PayLinkPage'))

const TENANT_LANDING_BRAND = {
  subtitle: 'Tenant Portal',
  heroTitle: (
    <>
      Rent, receipts &amp;
      <br />
      requests<span className="text-clay-400">.</span>
    </>
  ),
  heroTagline: 'Everything you need to stay on top of rent, deposits and maintenance — in one place.',
  valueProps: [
    {
      icon: BuildingOffice2Icon,
      title: 'One view of your unit',
      description: 'Current balance, upcoming rent and past receipts at a glance.',
    },
    {
      icon: ClipboardDocumentCheckIcon,
      title: 'M-PESA payments, instant receipt',
      description: 'Pay from your phone, and download the receipt once it is confirmed.',
    },
    {
      icon: CalendarDaysIcon,
      title: 'Payment plans when you need them',
      description: 'Agree instalments with your property manager instead of falling behind.',
    },
  ],
  payerNoun: 'tenants',
  beneficiaryNoun: 'unit',
  selectStepLabel: 'Select your property',
  crossPortalHint: 'Property manager?',
  operatorPortal: 'property' as const,
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
    </div>
  )
}

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Landing / token entry */}
        <Route path="/view" element={<LandingPage brand={TENANT_LANDING_BRAND} />} />

        {/* Magic pay link — outside TokenGate (self-authenticating) */}
        <Route path="/pay" element={<MagicPayPage />} />
        {/* Payment link: invoices, pay, receipts. Self-authenticating. */}
        <Route path="/l" element={<PayLinkPage notice={rentalPayLinkNotice} entityLabel={RENTAL_DEFAULT_CONTEXT.labels.entity} />} />

        {/* Authenticated tenant portal routes */}
        <Route
          path="/"
          element={
            <TokenGate>
              <RoleGate><VerticalModuleProvider module={rentalModule}>
                <PortalVerticalProvider>
                  <PortalLayout title="Tenant Portal" navItems={TENANT_NAV_ITEMS} headerActions={<RoleSwitch role="tenant" />} />
                </PortalVerticalProvider>
              </VerticalModuleProvider></RoleGate>
            </TokenGate>
          }
        >
          <Route index element={<BalancePage allowAcrossAccounts notice={rentalPayLinkNotice} entityLabel={RENTAL_DEFAULT_CONTEXT.labels.entity} />} />
          <Route path="receipts" element={<ReceiptsPage />} />
          <Route path="payment-plans" element={<PaymentPlansPage />} />
          <Route path="home" element={<MyTenancyPage />} />
          <Route path="maintenance" element={<MyRepairsPage />} />
          <Route path="messages" element={<MyMessagesPage />} />
        </Route>

        <Route path="/owner" element={<OwnerGate><VerticalModuleProvider module={rentalModule}><PortalVerticalProvider><PortalLayout title="Owner Portal" navItems={OWNER_NAV} headerActions={<RoleSwitch role="owner" />} /></PortalVerticalProvider></VerticalModuleProvider></OwnerGate>}>
          <Route index element={<OwnerUnitsPage />} />
          <Route path="decisions" element={<OwnerDecisionsPage />} />
          <Route path="decisions/:kind/:id" element={<OwnerDecisionsPage />} />
          <Route path="statement" element={<OwnerStatementPage />} />
          <Route path="payouts" element={<OwnerPayoutsPage />} />
          <Route path="repairs" element={<OwnerRepairsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/view" replace />} />
      </Routes>
    </Suspense>
  )
}

export default App
